/**
 * AdModal — imperative "openAdModal(feature)" so any callsite can await it.
 * Renders a 5-second simulated rewarded ad; on completion resolves(true).
 * Designed as a drop-in for AdMob later — replace `SimulatedAd` internals
 * with the native Capacitor plugin when the AdMob keys are available.
 */
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { showRewardedAd, isAdMobAvailable } from "@/lib/admob";

const AD_LENGTH_SEC = 5;

const FEATURE_LABEL = {
  tryons: "extra try-on",
  calendar_suggest: "outfit suggestion",
  grooming_gen: "grooming plan",
  makeup_gen: "makeup palette",
  chat_messages: "3 more chat messages",
  beauty_scan: "AI Scan",
  care_gen: "care routine",
};

let __openResolver = null;

export function openAdModal(feature, opts = {}) {
  return new Promise((resolve) => {
    // On native Android we skip the simulated modal entirely and show a real
    // AdMob rewarded ad. The promise resolves(true) only when the user
    // actually watched the ad (Rewarded event fired). Falls through to the
    // simulated web modal on any failure so free-tier gating never blocks
    // the user in a doom-loop when AdMob has no fill.
    //
    // `opts.ssvData` — server-issued customData blob attached to the ad
    // request so Google's SSV callback can bind the reward to this user+
    // feature. Comes from POST /ads/prepare.
    if (isAdMobAvailable()) {
      showRewardedAd({ customData: opts.ssvData || feature })
        .then((earned) => {
          if (earned) {
            resolve(true);
          } else {
            // If the native reward flow didn't return true, fall back to the
            // simulated 5s modal so the free user still gets their unlock.
            fallbackModal(feature, resolve);
          }
        })
        .catch(() => fallbackModal(feature, resolve));
      return;
    }
    fallbackModal(feature, resolve);
  });
}

function fallbackModal(feature, resolve) {
  __openResolver?.(false); // cancel any prior open modal
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const close = (val) => {
    __openResolver = null;
    root.unmount();
    host.remove();
    resolve(val);
  };
  __openResolver = close;
  root.render(<AdModalUi feature={feature} onClose={close} />);
}

function AdModalUi({ feature, onClose }) {
  const [remaining, setRemaining] = useState(AD_LENGTH_SEC);
  const timer = useRef(null);

  useEffect(() => {
    timer.current = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(timer.current);
  }, []);

  const done = remaining === 0;
  const label = FEATURE_LABEL[feature] || "this feature";

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/80 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ad-modal"
    >
      <div className="w-full max-w-md bg-background border border-border p-8 shadow-2xl relative">
        <button
          type="button"
          onClick={() => onClose(false)}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          aria-label="Close"
          data-testid="ad-modal-close"
        >
          <X size={18} />
        </button>
        <p className="eyebrow flex items-center gap-2"><Sparkles size={11} /> Stylebay · Sponsored</p>
        <p className="font-serif text-3xl mt-3 leading-tight">
          Watch a short ad<br /><em>to unlock {label}.</em>
        </p>

        {/* Simulated ad canvas */}
        <div
          className="mt-6 aspect-video bg-gradient-to-br from-accent to-foreground text-background flex flex-col items-center justify-center text-center p-6"
          data-testid="ad-modal-canvas"
        >
          <p className="text-[10px] uppercase tracking-[0.35em] opacity-70">Ad · 15s</p>
          <p className="font-serif text-3xl mt-3">Style meets craft.</p>
          <p className="text-xs mt-3 opacity-90 max-w-xs">Discover partner brands hand-picked to match your Style DNA.</p>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-muted overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-1000"
              style={{ width: `${((AD_LENGTH_SEC - remaining) / AD_LENGTH_SEC) * 100}%` }}
              data-testid="ad-modal-progress"
            />
          </div>
          <p className="text-xs tabular-nums w-10 text-right" data-testid="ad-modal-timer">
            {done ? "OK" : `${remaining}s`}
          </p>
        </div>

        <Button
          disabled={!done}
          onClick={() => onClose(true)}
          className="w-full mt-6 rounded-none uppercase tracking-[0.22em] text-xs h-11"
          data-testid="ad-modal-collect"
        >
          {done ? "Collect reward" : "Please wait…"}
        </Button>

        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-4 text-center">
          Upgrade to Premium to skip ads forever.
        </p>
      </div>
    </div>
  );
}
